/**
 * 认证与工作台 —— 与后端 `admin/controllers/auth.controller.ts`、`dashboard.controller.ts` 对齐
 *
 *   POST /api/admin/auth/login           账号密码登录
 *   POST /api/admin/auth/logout          注销（服务端吊销当前 token）
 *   GET  /api/admin/auth/profile         当前管理员 + 权限列表（前端据此生成菜单）
 *   GET  /api/admin/dashboard?trendDays=7|30
 */
import type { AdminDashboardRes, PageQuery, PageResult } from '@weidian/contracts'
import { get, post } from '@/api/request'
import { unwrapList } from '@/api/unwrap'

/**
 * 登录响应。
 * ⚠️ 后端是**扁平结构**：管理员信息与 token 同级，没有嵌套的 `profile` 字段。
 *    前端在 store 里拆成 token + profile 两份状态。
 */
export interface AdminLoginRes {
  id: number
  username: string
  realName: string
  roleCode: string
  roleName: string
  permissions: string[]
  accessToken: string
  expiresIn: number
}

export interface AdminLoginReq {
  username: string
  password: string
  captcha?: string
}

/** POST /admin/auth/login —— 连续失败 5 次锁定 15 分钟（服务端逻辑） */
export function adminLogin(payload: AdminLoginReq): Promise<AdminLoginRes> {
  return post<AdminLoginRes>('/admin/auth/login', payload, { silent401: true, showError: true })
}

/** POST /admin/auth/logout —— 服务端把当前 token 加入吊销名单，立即失效 */
export function adminLogout(): Promise<{ revoked: boolean }> {
  return post<{ revoked: boolean }>('/admin/auth/logout', undefined, { showError: false })
}

/** GET /admin/auth/profile —— 返回 { id, username, realName, roleCode, roleName, permissions } */
export function fetchAdminProfile(): Promise<{
  id: number
  username: string
  realName: string
  roleCode: string
  roleName: string
  permissions: string[]
}> {
  return get('/admin/auth/profile', undefined, { showError: false })
}

/**
 * GET /admin/dashboard
 *
 * · `trendDays` 只支持 7 或 30（**趋势图**天数）；
 * · `startTime` / `endTime` 是四张卡片的**统计区间**（2026-10-30 需求）：
 *   `YYYY-MM-DD`；只填日期时后端按"含当天全天"处理（结束端算到次日 0 点）；都不传 = 今日。
 */
export function fetchDashboard(params?: {
  trendDays?: 7 | 30
  startTime?: string
  endTime?: string
}): Promise<AdminDashboardRes> {
  return get<AdminDashboardRes>('/admin/dashboard', params)
}

// ==================== A26 管理员账号 ====================

/** 后端 admin-user/list 返回项（字段来自 AdminUserService.list） */
export interface AdminUserView {
  id: number
  username: string
  realName: string
  phone: string | null
  roleId: number
  roleCode: string
  roleName: string
  status: number
  lastLoginAt: string | null
  lastLoginIp: string | null
  createdAt: string
}

export interface AdminUserQuery extends PageQuery {
  username?: string
  roleId?: number
  status?: number
}

export interface AdminUserSaveReq {
  id?: number
  username: string
  realName: string
  phone?: string | null
  roleId: number
  /** 新建必填（后端长度 6-64）；编辑时留空表示不改密码 */
  password?: string
  status?: number
}

export function fetchAdminUsers(params: AdminUserQuery): Promise<PageResult<AdminUserView>> {
  return get<PageResult<AdminUserView>>('/admin/admin-user/list', params)
}

export function saveAdminUser(payload: AdminUserSaveReq): Promise<{ id: number }> {
  return post<{ id: number }>('/admin/admin-user/save', payload)
}

export function updateAdminUserStatus(payload: { id: number; status: number; reason?: string }): Promise<void> {
  return post<void>('/admin/admin-user/status', payload)
}

export function resetAdminUserPassword(payload: { id: number; password: string; note?: string }): Promise<void> {
  return post<void>('/admin/admin-user/reset-password', payload)
}

export function deleteAdminUser(payload: { id: number }): Promise<void> {
  return post<void>('/admin/admin-user/delete', payload)
}

// ==================== A27 角色权限 ====================

export interface AdminRoleView {
  id: number
  code: string
  name: string
  /** 后端存的是 JSON 数组字符串，服务层已解析为 string[] */
  permissions: string[]
  adminCount?: number
  createdAt?: string
}

export interface AdminRoleSaveReq {
  id?: number
  code: string
  name: string
  permissions: string[]
}

export function fetchRoles(): Promise<AdminRoleView[]> {
  // ⚠️ 后端返回 { list: [...] }（不是裸数组）—— 不解包会让角色页整个渲染不出来
  return get<unknown>('/admin/role/list').then((r) => unwrapList<AdminRoleView>(r))
}

export function saveRole(payload: AdminRoleSaveReq): Promise<{ id: number }> {
  return post<{ id: number }>('/admin/role/save', payload)
}

export function deleteRole(payload: { id: number }): Promise<void> {
  return post<void>('/admin/role/delete', payload)
}

/**
 * 可勾选的权限点分组。
 * ⚠️ 后端**未提供** `/admin/role/permissions` 接口，权限码清单由后端
 * `AdminPermission` 枚举维护。前端在 `utils/permission.ts` 里维护一份镜像清单
 * 用于渲染勾选树；提交时后端会用 `filterKnownPermissions()` 过滤未知码。
 * （已作为契约层需求记录在交付说明中。）
 */
export const PERMISSION_TREE_FALLBACK: ReadonlyArray<{
  group: string
  groupLabel: string
  items: ReadonlyArray<{ code: string; label: string }>
}> = [
  { group: 'dashboard', groupLabel: '工作台', items: [{ code: 'dashboard:view', label: '查看工作台' }] },
  {
    group: 'product',
    groupLabel: '商品与分类',
    items: [
      { code: 'product:view', label: '商品查看' },
      { code: 'product:edit', label: '商品编辑' },
      { code: 'product:delete', label: '商品删除' },
      { code: 'category:view', label: '分类查看' },
      { code: 'category:edit', label: '分类编辑' },
    ],
  },
  {
    // ⚠️ 本组是「分类改成活动列表」需求新增的。同理：后端 AdminPermission 加了码、
    //    前端勾选树没有的话，超管之外的角色在界面上**勾不到**活动权限 →
    //    运营配不了活动（后端 @RequirePermission 一直拒 403），
    //    而界面上看不出任何异常。
    group: 'activity',
    groupLabel: '营销活动',
    items: [
      { code: 'activity:view', label: '活动查看（列表 / 详情 / 关联商品）' },
      { code: 'activity:edit', label: '活动编辑（新建 / 上下架 / 排序 / 删除 / 关联商品）' },
    ],
  },
  {
    group: 'order',
    groupLabel: '订单',
    items: [
      { code: 'order:view', label: '订单查看（含回调日志）' },
      { code: 'order:price', label: '改价' },
      { code: 'order:address', label: '修改收货地址' },
      { code: 'order:remark', label: '添加备注' },
      { code: 'order:ship', label: '发货 / 批量发货' },
      { code: 'order:close', label: '关闭订单' },
      { code: 'order:export', label: '订单导出' },
    ],
  },
  {
    group: 'refund',
    groupLabel: '售后',
    items: [
      { code: 'refund:view', label: '售后查看' },
      { code: 'refund:audit', label: '售后审核' },
      { code: 'refund:execute', label: '执行退款' },
    ],
  },
  {
    // ⚠️ 这组是 2026-09 补上的：后端 AdminPermission 里早就有这四个码，
    //    前端勾选树没有 → 超管之外的角色在界面上**勾不到**取号权限 →
    //    客服/运营实际用不了快递100 取号（后端 @RequirePermission 一直拒）。
    //    「取号」与「发货」故意分开：取号真实消耗面单并产生面单费，
    //    只给发货权限的人不该能重复取号烧钱。
    group: 'express',
    groupLabel: '快递100 电子面单',
    items: [
      { code: 'express:view', label: '面单查看（记录与配置状态、打印）' },
      { code: 'express:waybill-create', label: '取号并打印（★ 消耗面单并产生费用）' },
      { code: 'express:waybill-cancel', label: '作废面单（回收单号）' },
      { code: 'express:setting-edit', label: '快递100 设置（发货人 / 模板 / 默认内件）' },
    ],
  },
  {
    // ⚠️ 这一组是 2026-09 补上的：后端 AdminPermission 里早就有这两个码，
    //    前端勾选树没有 → 超管之外的角色在界面上**勾不到**邀请码权限 →
    //    运营实际发不了码（后端 @RequirePermission 一直拒 403）。
    //    「看」与「生成」故意分开：生成邀请码 = 放人进来，
    //    是会员注册链路上唯一的准入动作。
    group: 'invite',
    groupLabel: 'VIP 升级码（活动商品查看权限凭证）',
    items: [
      { code: 'invite:view', label: '升级码查看（列表）' },
      {
        code: 'invite:edit',
        label: '生成 / 停用 / 删除 VIP 升级码（★ 发一张码 = 放一个 VIP 进来）',
      },
    ],
  },
  {
    group: 'member',
    groupLabel: '会员与营销',
    items: [
      { code: 'member:view', label: '会员查看' },
      { code: 'member:points', label: '调整积分' },
      { code: 'member:edit', label: '会员编辑（标签/拉黑）' },
      { code: 'member:merge', label: '账号合并（不可逆）' },
      { code: 'level:view', label: '等级查看' },
      { code: 'level:edit', label: '等级编辑' },
      { code: 'points-rule:view', label: '积分规则查看' },
      { code: 'points-rule:edit', label: '积分规则编辑' },
      { code: 'coupon:view', label: '优惠券查看' },
      { code: 'coupon:edit', label: '优惠券编辑' },
      { code: 'coupon:grant', label: '优惠券定向发放' },
    ],
  },
  {
    group: 'pay',
    groupLabel: '支付与对账',
    items: [
      { code: 'pay-channel:view', label: '收银台配置查看' },
      { code: 'pay-channel:toggle', label: '收银台通道开关（应急摘除）' },
      { code: 'pay-channel:priority', label: '通道展示顺序' },
      { code: 'reconcile:view', label: '对账查看' },
      { code: 'reconcile:run', label: '手动触发对账' },
      { code: 'reconcile:handle', label: '差异处理' },
      { code: 'reconcile:export', label: '差异导出' },
    ],
  },
  {
    group: 'content',
    groupLabel: '装修与内容',
    items: [
      { code: 'page:view', label: '首页装修查看' },
      { code: 'page:edit', label: '首页装修编辑' },
      { code: 'banner:view', label: 'Banner 查看' },
      { code: 'banner:edit', label: 'Banner 编辑' },
      { code: 'content:view', label: '内容查看' },
      { code: 'content:edit', label: '内容编辑' },
      { code: 'freight:view', label: '运费模板查看' },
      { code: 'freight:edit', label: '运费模板编辑' },
    ],
  },
  {
    group: 'system',
    groupLabel: '设置与权限',
    items: [
      { code: 'setting:view', label: '系统设置查看' },
      { code: 'setting:edit', label: '系统设置编辑' },
      { code: 'setting:secret', label: '密钥修改（单向写入）' },
      { code: 'admin-user:view', label: '管理员账号查看' },
      { code: 'admin-user:edit', label: '管理员账号编辑' },
      { code: 'role:view', label: '角色查看' },
      { code: 'role:edit', label: '角色权限编辑' },
      { code: 'log:view', label: '审计日志查看' },
    ],
  },
]

/**
 * 取权限点清单：优先问后端，后端没有该接口时回落到本地镜像清单。
 * 这样后端将来补上 `/admin/role/permissions` 也无需改前端。
 */
export async function fetchPermissionTree(): Promise<
  Array<{ group: string; groupLabel: string; items: Array<{ code: string; label: string }> }>
> {
  try {
    const remote = await get<
      Array<{ group: string; groupLabel: string; items: Array<{ code: string; label: string }> }>
    >('/admin/role/permissions', undefined, { showError: false })
    if (Array.isArray(remote) && remote.length > 0) return remote
  } catch {
    // 后端暂无此接口（返回 1004 未实现）—— 用本地镜像清单
  }
  return PERMISSION_TREE_FALLBACK.map((g) => ({
    group: g.group,
    groupLabel: g.groupLabel,
    items: g.items.map((i) => ({ code: i.code, label: i.label })),
  }))
}

// ==================== A28 审计日志 ====================

export interface AuditLogView {
  id: number
  adminId: number
  adminName: string
  module: string
  action: string
  targetId: string | null
  beforeJson: unknown
  afterJson: unknown
  ip: string | null
  createdAt: string
}

export interface AuditLogQuery extends PageQuery {
  adminId?: number
  module?: string
  action?: string
  targetId?: string
  startTime?: string
  endTime?: string
}

export function fetchAuditLogs(params: AuditLogQuery): Promise<PageResult<AuditLogView>> {
  return get<PageResult<AuditLogView>>('/admin/log/list', params)
}

/** 按对象（如订单号）查日志 */
export function fetchLogsByTarget(params: { targetId: string; take?: number }): Promise<AuditLogView[]> {
  // ⚠️ 后端返回 { list: [...] }（不是裸数组）
  return get<unknown>('/admin/log/by-target', params).then((r) => unwrapList<AuditLogView>(r))
}
